import { resetDirty } from "@frui.ts/dirtycheck";
import type { PropertyName } from "@frui.ts/helpers";
import { bound } from "@frui.ts/helpers";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { attachAutomaticValidator, hideValidationErrors, validate } from "@frui.ts/validation";
import type { EntityNames } from "config";
import { Modules } from "config";
import type ChargeOrderSearchResponse from "entities/chargeOrderSearchResponse";
import type ChargeResponse from "entities/chargeResponse";
import type Contact from "entities/contact";
import ChargeValidity from "entities/chargeValidity";
import ChargeOrderStatus from "entities/chargeOrderStatus";
import PaymentType from "entities/paymentType";
import SourceType from "entities/sourceType";
import ChargeOrderSearchFilter, { ChargeOrderSearchFilterValidationRules } from "entities/filters/chargeOrderSearchFilter";
import type LicensePlate from "entities/licensePlate";
import type Payment from "entities/payment";
import omit from "lodash/omit";
import { computed, reaction } from "mobx";
import type EnumService from "services/enumService";
import type OptionsService from "services/optionsService";
import emptyListResponse from "utils/emptyListResponse";
import { isFilterEmpty } from "utils/filters";
import type CountrySelectViewModel from "../../countrySelectViewModel";
import ListViewModel from "../../listViewModel";
import { allowToPair } from "./rights";

export default abstract class ChargeOrderSearchListViewModel<TDetail extends ScreenBase = ScreenBase> extends ListViewModel<
  ChargeOrderSearchResponse,
  TDetail,
  ScreenBase,
  ChargeOrderSearchFilter
> {
  readonly modelName = "chargeOrderSearch";
  readonly idProperty = "searchId";
  protected abstract enumService: EnumService;
  protected abstract optionsService: OptionsService;
  protected abortController?: AbortController;
  countrySelectViewModel: CountrySelectViewModel;

  constructor(protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>) {
    super();
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  onInitialize() {
    this.unsubscriptions.push(
      reaction(
        () => [
          this.filter.licensePlate,
          this.filter.orderIdent,
          this.filter.email,
          this.filter.phoneNumber,
          this.filter.countryId,
          this.filter.orderStatus,
          this.filter.orderCreatedDate,
          this.filter.validFor,
          this.filter.paidAmount,
          this.filter.validSinceDate,
          this.filter.validUntilDate,
          this.filter.sourceType,
          this.filter.paymentType,
        ],
        () => {
          this.filter.fullTextSearchEnabled = false;
        }
      )
    );
    super.onInitialize();
  }

  @computed
  get allowDelete() {
    return false;
  }

  @computed
  get allowToPair() {
    return allowToPair(this.securityService);
  }

  @computed
  get orderStateOptions() {
    return this.optionsService.internalEnumOptions(ChargeOrderStatus);
  }

  @computed
  get sourceTypeOptions() {
    return this.optionsService.internalEnumOptions(SourceType);
  }

  @computed
  get paymentTypeOptions() {
    return this.optionsService.internalEnumOptions(PaymentType);
  }

  @computed
  get chargeValidityOptions() {
    return this.optionsService.internalEnumOptions(ChargeValidity);
  }

  countryCode(countryId: string) {
    return this.enumService.value("countries", countryId)?.code ?? "";
  }

  @computed
  get filterIsNotEmpty() {
    return !Object.values(omit(this.appliedFilter, "fullTextSearchEnabled") || {}).every(item => item === undefined);
  }

  @bound
  onItemClick() {
    const detailFactory = this.detailViewModelFactory;
    if (detailFactory) {
      return (item: ChargeOrderSearchResponse) => this.tryActivateChild(detailFactory(item.id));
    } else {
      return undefined;
    }
  }

  protected async fetchData() {
    if (this.filterIsNotEmpty) {
      const { limit, offset } = this.pagingFilter;
      this.abortController = new AbortController();
      const data = await this.repository.getAllChargeOrderSearch(
        { offset, limit },
        this.appliedFilter,
        this.abortController.signal
      );
      this.abortController = undefined;
      return data;
    } else {
      return emptyListResponse();
    }
  }

  protected processData(items: ChargeOrderSearchResponse[]) {
    return this.enumService.proxyObjectEnums(items);
  }

  protected createFilter() {
    return attachAutomaticValidator(new ChargeOrderSearchFilter(), ChargeOrderSearchFilterValidationRules);
  }

  protected resetFilterValues(filter: ChargeOrderSearchFilter) {
    super.resetFilterValues(filter);
    hideValidationErrors(this.filter);

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }
  }

  applyFilter() {
    if (!isFilterEmpty(omit(this.filter, "fullTextSearchEnabled")) && !validate(this.filter)) {
      return false;
    }

    this.appliedFilter = this.cloneFilterForApply(this.filter);
    this.pagingFilter.offset = 0;
    resetDirty(this.filter);
    return true;
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `paymentsPairing.${code}`, size);
  }

  translateState(status: ChargeOrderStatus) {
    return this.localizationService.translateEnum("chargeOrderStatus", status);
  }

  translateSourceType(sourceType: SourceType) {
    return this.localizationService.translateEnum("sourceType", sourceType);
  }

  translatePaymentType(paymentType: PaymentType) {
    return this.localizationService.translateEnum("paymentType", paymentType);
  }

  translateSubAttribute(
    entityName: "charge",
    attribute: PropertyName<ChargeOrderSearchResponse> | PropertyName<ChargeResponse>
  ): string;
  translateSubAttribute(entityName: "payment", attribute: PropertyName<Payment>): string;
  translateSubAttribute(entityName: "contact", attribute: PropertyName<Contact>): string;
  translateSubAttribute(entityName: "licensePlate", attribute: PropertyName<LicensePlate>): string;
  translateSubAttribute(entityName: EntityNames, attribute: string) {
    return this.localizationService.translateAttribute(entityName, attribute);
  }
}
